import { Breed } from 'src/app/core/models/breed.model';
import { SpeciesService } from 'src/app/core/services/species.service';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { ChangeDetectionStrategy, Component, Input, OnInit, OnDestroy, forwardRef } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isNil, isString, orderBy, startCase, toLower, values } from 'lodash';
import { distinctUntilChanged, map, take, shareReplay, startWith, filter, switchMap } from 'rxjs/operators';
import { untilDestroy } from '@ngrx-utils/store';
import { Species } from 'src/app/core/models/species.model';

@Component({
  selector: 'app-breed-form-field',
  templateUrl: './breed-form-field.component.html',
  styleUrls: ['./breed-form-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BreedFormFieldComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BreedFormFieldComponent implements OnInit, OnDestroy {

  species$ = this.speciesService.species$;

  @Input()
  set selectedSpeciesName(selectedSpeciesName: string) {
    this.selectedSpeciesName$.next(selectedSpeciesName);
  }

  input = new FormControl();
  selectedSpeciesName$ = new BehaviorSubject<string | null>(null);
  selectedSpecies$ = this.getSelectedSpecies();
  breeds$ = this.getBreeds();
  filteredBreeds$ = this.getFilteredBreeds();

  showRemove$ = new BehaviorSubject<string | null>(null);
  showAddBreed$ = this.getShowAddBreed();

  constructor(
    private speciesService: SpeciesService
  ) { }

  ngOnInit(): void {
    this.input.valueChanges.pipe(
      map(value => this.toTitleCase(isString(value) ? value : value?.name)),
      distinctUntilChanged(),
      untilDestroy(this)
    ).subscribe(value => this.onChange(value));
  }

  ngOnDestroy(): void { }

  writeValue(breedName: string): void {
    this.input.setValue(breedName);
  }

  onChange = (_) => { };

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  displayFn(breed: any): string {
    return breed?.name ? breed.name : breed;
  }

  addBreed(name: string) {
    this.selectedSpecies$.pipe(
      take(1)
    ).subscribe(selectedSpecies => {
      this.speciesService.addBreed(selectedSpecies, name);
    });
  }

  onChipTap(breed: Breed, event) {
    if (event.target.localName === 'mat-chip') {
      this.input.patchValue(breed);
      this.selectedSpecies$.pipe(
        take(1)
      ).subscribe(selectedSpecies => {
        this.speciesService.increaseBreedFrequency(selectedSpecies, breed);
      });
    }
  }

  isSelected(breed: Breed): boolean {
    const value = this.input.value;
    if (isString(value)) {
      return breed.name.toLowerCase() === value.toLowerCase();
    }
    return breed.name.toLowerCase() === value?.value.toLowerCase();
  }

  onChipPress(breed) {
    if (!breed.default) {
      this.showRemove$.next(breed.value);
    }
  }

  showRemove(breed): boolean {
    return breed.value === this.showRemove$.getValue();
  }

  removeBreed(breed) {
    this.selectedSpecies$.pipe(
      take(1)
    ).subscribe(selectedSpecies => {
      this.speciesService.removeBreed(selectedSpecies, breed);
    });
  }

  getShowAddBreed(): Observable<boolean> {
    return combineLatest([
      this.input.valueChanges.pipe(map(value => isString(value) ? value : value?.name)),
      this.breeds$
    ]).pipe(
      map(([value, breeds]) => {
        if (!value || !this.checkIfValueExists(value, breeds)) {
          return false;
        }
        return true;
      })
    );
  }

  checkIfValueExists(value: string, breeds: Breed[]): boolean {
    return !breeds.find(b => b.name.toLowerCase() === value?.toLowerCase());
  }

  private getSelectedSpecies(): Observable<Species | null> {
    return combineLatest([
      this.species$,
      this.selectedSpeciesName$
    ]).pipe(
      map(([species, selectedSpeciesName]) => {
        return species.find(s => s.name.toLowerCase() === selectedSpeciesName?.toLowerCase());
      }),
      shareReplay(1)
    );
  }

  private getBreeds(): Observable<Breed[]> {
    return this.selectedSpecies$.pipe(
      map(selectedSpecies => orderBy(values(selectedSpecies?.breeds || {}), 'frequency', 'desc')),
      shareReplay(1)
    );
  }

  private getFilteredBreeds(): Observable<Breed[]> {
    return this.input.valueChanges.pipe(
      distinctUntilChanged(),
      startWith(''),
      filter(searchTerm => isString(searchTerm) || isNil(searchTerm)),
      switchMap(searchTerm => this.breeds$.pipe(
        map(breeds => breeds.filter(b => b?.name?.toLowerCase().includes(searchTerm.toLowerCase())))
      )),
      shareReplay(1)
    );
  }

  toTitleCase(string: string): string {
    return startCase(toLower(string));
  }

}
